import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';

/**
 * FeatureSettingsSection — org-level controls, shown in the Settings tab of
 * User Management, for two behaviors that used to be scattered per-user
 * localStorage toggles:
 *
 *   • Clean-trigger widgets — which client widgets flip a profile to
 *     "not clean" when edited.
 *   • Suggestion scan mode — whether an AI note-scan runs automatically
 *     after a call's transcription completes, or only when run by hand.
 *
 * These are the organization DEFAULTS. Individual users can still override
 * them for themselves elsewhere; this sets the baseline everyone starts from.
 */

const WIDGET_LABELS = {
  positions:     'Positions / employment',
  education:     'Education & certifications',
  compensation:  'Compensation',
  contacts:      'Contacts & links',
  preferences:   'Preferences',
  tracking:      'Candidate tracking',
  custom_fields: 'Custom fields',
};

const auth = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('authToken')}`,
});

/**
 * Read a response as JSON only when it really is JSON.
 *
 * When the API routes aren't deployed, the host serves the SPA's index.html
 * as a fallback — sometimes with a 200 — so `res.ok` passes and `.json()`
 * then dies on "Unexpected token '<'". Checking content-type turns that into
 * a clear "not deployed yet" state instead of a cryptic parse error.
 */
async function readJson(res) {
  const type = res.headers.get('content-type') || '';
  if (!type.includes('application/json')) return { notJson: true };
  try {
    return { data: await res.json() };
  } catch {
    return { notJson: true };
  }
}

const FeatureSettingsSection = () => {
  const [options, setOptions] = useState(null);
  const [cleanWidgets, setCleanWidgets] = useState([]);
  const [scanTriggers, setScanTriggers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [optRes, orgRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/feature-settings/options`, { headers: auth() }),
          fetch(`${API_BASE_URL}/api/feature-settings/org`, { headers: auth() }),
        ]);
        const opt = await readJson(optRes);
        const org = await readJson(orgRes);

        if (opt.notJson || org.notJson || orgRes.status === 404) {
          setUnavailable(true);
          return;
        }
        if (opt.data) setOptions(opt.data);
        if (orgRes.ok && org.data) {
          setCleanWidgets(org.data.clean_trigger_widgets || []);
          setScanTriggers(org.data.suggestions_scan_triggers || []);
        }
      } catch (e) {
        console.error('Failed to load feature settings:', e);
        setUnavailable(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleTrigger = (key) => {
    setScanTriggers(prev =>
      prev.includes(key) ? prev.filter(t => t !== key) : [...prev, key]
    );
  };

  const toggleWidget = (key) => {
    setCleanWidgets(prev =>
      prev.includes(key) ? prev.filter(w => w !== key) : [...prev, key]
    );
  };

  const save = async () => {
    setSaving(true);
    setMsg('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/feature-settings/org`, {
        method: 'PUT',
        headers: auth(),
        body: JSON.stringify({
          clean_trigger_widgets: cleanWidgets,
          suggestions_scan_triggers: scanTriggers,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Save failed');
      setMsg('Settings saved');
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      setMsg('Error: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border p-6">
        <div className="animate-pulse text-gray-400 text-sm">Loading feature settings…</div>
      </div>
    );
  }

  if (unavailable) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-2xl">
        <p className="text-sm text-amber-900 font-medium">Feature settings aren't available on this server yet.</p>
        <p className="text-xs text-amber-800 mt-1">
          The API for this section needs deploying: <code className="bg-amber-100 px-1 rounded">server/src/routes/featureSettings.js</code>,{' '}
          <code className="bg-amber-100 px-1 rounded">server/src/services/orgFeatureSettings.js</code>, and the updated{' '}
          <code className="bg-amber-100 px-1 rounded">server.js</code>.
        </p>
      </div>
    );
  }

  const widgetKeys = options?.clean_widgets || Object.keys(WIDGET_LABELS);

  return (
    <div className="space-y-4 max-w-2xl">
      {/* Clean-trigger widgets */}
      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Profile review flag</h2>
            <p className="text-sm text-gray-500">
              Choose which widgets mark a candidate profile as "needs review" when their data is edited.
              A flagged profile loses the green check in the candidate list until someone re-verifies it.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {widgetKeys.map(key => (
            <label key={key} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                checked={cleanWidgets.includes(key)}
                onChange={() => toggleWidget(key)}
                className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">{WIDGET_LABELS[key] || key}</span>
            </label>
          ))}
        </div>
        {cleanWidgets.length === 0 && (
          <p className="mt-3 text-xs text-amber-600">
            Nothing selected — editing candidate data will never flag a profile for review.
          </p>
        )}
      </div>

      {/* Suggestion scan mode */}
      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">AI suggestion scanning</h2>
            <p className="text-sm text-gray-500">
              When the AI scans candidate notes to suggest profile updates.
            </p>
          </div>
        </div>

        <p className="text-xs text-gray-500 mb-3">
          Pick the events that start a scan. Leave all unchecked for manual-only — scans then run
          just from the "Scan notes" button in the Suggestions panel.
        </p>

        <div className="space-y-2">
          {[
            {
              key: 'on_transcription',
              label: 'After a call is transcribed',
              hint: 'The transcript is in the notes by then, so the scan sees what was actually said.',
            },
            {
              key: 'on_note',
              label: 'After a note is saved',
              hint: 'Catches intel typed straight into a note without a call.',
            },
            {
              key: 'on_mention',
              label: 'When a candidate is @-mentioned',
              hint: 'Scans the mentioned candidate, whose own profile may have gained information.',
            },
          ].map(t => (
            <label key={t.key} className="flex items-start gap-3 px-3 py-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                checked={scanTriggers.includes(t.key)}
                onChange={() => toggleTrigger(t.key)}
                className="mt-0.5 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <span>
                <span className="block text-sm font-medium text-gray-800">{t.label}</span>
                <span className="block text-xs text-gray-500">{t.hint}</span>
              </span>
            </label>
          ))}
        </div>

        {scanTriggers.length === 0 && (
          <p className="mt-3 text-xs text-gray-500">
            Manual only — no automatic spend.
          </p>
        )}

        {scanTriggers.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Each enabled event scans independently, straight away. A scan costs roughly 1-2 cents,
              so a busy recruiter runs well under a dollar a day. Spend limits are the real ceiling —
              a system administrator sets those under Settings &rarr; System.
            </p>
            <p className="text-xs text-gray-500 mt-1">
              You'll get a notification when suggestions are ready, so you don't have to stay on the profile.
            </p>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save feature settings'}
        </button>
        {msg && (
          <span className={`text-sm ${msg.startsWith('Error') ? 'text-red-600' : 'text-green-600'}`}>{msg}</span>
        )}
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <p className="text-xs text-gray-600">
          These are organization defaults. Individual users can override them for their own sessions;
          this sets the baseline everyone starts from.
        </p>
      </div>
    </div>
  );
};

export default FeatureSettingsSection;
